import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Loader2, StickyNote, Plus, Check, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { showError } from "../lib/errorHandler";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_META = {
  mold: { label: "Molde", color: "bg-indigo-100 text-indigo-700 border-indigo-300" },
  machine: { label: "Máquina", color: "bg-blue-100 text-blue-700 border-blue-300" },
  quality: { label: "Qualidade", color: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  material: { label: "Matéria-Prima", color: "bg-amber-100 text-amber-700 border-amber-300" },
  general: { label: "Geral", color: "bg-slate-100 text-slate-700 border-slate-300" },
};

const PRIORITY_META = {
  info: { label: "Info", color: "bg-blue-500 text-white", icon: "ℹ" },
  warning: { label: "Aviso", color: "bg-amber-500 text-white", icon: "⚠" },
  urgent: { label: "URGENTE", color: "bg-rose-600 text-white animate-pulse", icon: "🚨" },
};

export default function ShiftNotes({ orderId, machineId, compact = false }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ category: "general", priority: "info", title: "", note: "" });
  const [showResolved, setShowResolved] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (orderId) params.set("order_id", orderId);
      if (machineId) params.set("machine_id", machineId);
      if (!showResolved) params.set("resolved", "false");
      const r = await axios.get(`${API}/shift-notes?${params.toString()}`);
      setNotes(r.data || []);
    } catch (e) { showError(e, "Erro"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [orderId, machineId, showResolved]);

  const submit = async () => {
    if (!form.title.trim() || !form.note.trim()) { toast.error("Preencha título e nota"); return; }
    try {
      await axios.post(`${API}/shift-notes`, { ...form, order_id: orderId || null, machine_id: machineId || null });
      toast.success("Nota adicionada");
      setForm({ category: "general", priority: "info", title: "", note: "" });
      setShowNew(false);
      load();
    } catch (e) { showError(e, "Erro"); }
  };

  const resolve = async (id) => {
    const comment = window.prompt("Comentário de resolução (opcional):");
    try {
      await axios.post(`${API}/shift-notes/${id}/resolve`, { resolution_comment: comment });
      toast.success("Nota resolvida");
      load();
    } catch (e) { showError(e, "Erro"); }
  };

  const del = async (id) => {
    if (!window.confirm("Apagar esta nota?")) return;
    try { await axios.delete(`${API}/shift-notes/${id}`); load(); }
    catch (e) { showError(e, "Erro"); }
  };

  return (
    <div className={compact ? "space-y-2" : "p-4 space-y-3"} data-testid="shift-notes">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <StickyNote className="h-4 w-4 text-amber-600" />
          <span className="font-bold text-sm">Notas entre Turnos</span>
          {notes.filter(n => !n.resolved && n.priority === "urgent").length > 0 && (
            <span className="bg-rose-600 text-white text-[10px] px-2 py-0.5 rounded-full font-bold animate-pulse">
              {notes.filter(n => !n.resolved && n.priority === "urgent").length} URGENTE(S)
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <label className="text-[10px] flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={showResolved} onChange={e => setShowResolved(e.target.checked)} />
            resolvidas
          </label>
          <Button size="sm" variant="outline" onClick={() => setShowNew(!showNew)} data-testid="btn-new-note">
            <Plus className="h-3.5 w-3.5 mr-1" />Nova
          </Button>
        </div>
      </div>

      {showNew && (
        <div className="border-2 border-blue-300 bg-blue-50 rounded-lg p-3 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Categoria</Label>
              <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="w-full h-8 border rounded px-2 text-xs">
                {Object.entries(CATEGORY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Prioridade</Label>
              <select value={form.priority} onChange={e => setForm({...form, priority: e.target.value})} className="w-full h-8 border rounded px-2 text-xs" data-testid="note-priority">
                <option value="info">Info</option><option value="warning">Aviso</option><option value="urgent">URGENTE</option>
              </select>
            </div>
          </div>
          <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Título (ex: Bico quente cav. 2)" className="h-8 text-sm" data-testid="note-title" />
          <Textarea value={form.note} onChange={e => setForm({...form, note: e.target.value})} placeholder="Descrição detalhada..." rows={2} className="text-sm" data-testid="note-body" />
          <div className="flex gap-2">
            <Button size="sm" onClick={submit} className="flex-1" data-testid="btn-submit-note">Guardar</Button>
            <Button size="sm" variant="outline" onClick={() => setShowNew(false)}>Cancelar</Button>
          </div>
        </div>
      )}

      {loading ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : (
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {notes.length === 0 && <div className="text-xs text-center text-muted-foreground py-4">Sem notas ativas</div>}
          {notes.map(n => {
            const cat = CATEGORY_META[n.category] || CATEGORY_META.general;
            const pri = PRIORITY_META[n.priority] || PRIORITY_META.info;
            return (
              <div key={n.id} className={`border-l-4 ${n.resolved ? "opacity-60 border-slate-300" : n.priority === "urgent" ? "border-rose-500" : n.priority === "warning" ? "border-amber-500" : "border-blue-500"} bg-white rounded p-2 shadow-sm`} data-testid={`note-${n.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-1 flex-wrap">
                    <span className={`text-[9px] px-1.5 py-0.5 rounded border font-bold ${cat.color}`}>{cat.label}</span>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${pri.color}`}>{pri.icon} {pri.label}</span>
                    {n.resolved && <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-bold">✓ RESOLVIDA</span>}
                  </div>
                  <div className="flex gap-1">
                    {!n.resolved && (
                      <button onClick={() => resolve(n.id)} className="text-emerald-600 hover:bg-emerald-50 p-1 rounded" title="Marcar resolvida" data-testid={`resolve-${n.id}`}>
                        <Check className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button onClick={() => del(n.id)} className="text-rose-500 hover:bg-rose-50 p-1 rounded" title="Apagar">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                <div className="font-bold text-sm mt-1">{n.title}</div>
                <div className="text-xs text-slate-700 whitespace-pre-line">{n.note}</div>
                <div className="text-[10px] text-slate-400 mt-1">
                  Por <strong>{n.created_by_name || n.created_by}</strong> · {new Date(n.created_at).toLocaleString("pt-PT")}
                  {n.resolved && n.resolved_by && <> · Resolvida por <strong>{n.resolved_by_name || n.resolved_by}</strong></>}
                </div>
                {n.resolved && n.resolution_comment && <div className="text-[10px] italic text-emerald-700 mt-1">"{n.resolution_comment}"</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
